import { useState } from 'react';
import { CertificationInterestForm } from '../components/forms/CertificationInterestForm';

const RoadmapPage = () => {
  const [showCertForm, setShowCertForm] = useState(false);

  return (
    <div data-testid="roadmap-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              Standard Adoption Pathway
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              SAFE is not a product roadmap.
            </p>
            <p className="text-lg md:text-xl text-slate-300">
              It is the pathway through which independent verification becomes the standard for AI safety.
            </p>
          </div>
        </div>
      </section>

      {/* Phase 1 */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Phase 1</p>
            <h2 className="heading-2 mb-8">Standard Definition</h2>
            <p className="text-lg text-slate-700 mb-6">
              SAFE defines what constitutes safety in AI systems operating in child-facing and high-risk environments.
            </p>
            <p className="text-lg text-slate-700 mb-2">Safety is no longer abstract.</p>
            <p className="text-lg text-slate-900 font-semibold">It is defined.</p>
          </div>
        </div>
      </section>

      {/* Phase 2 */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Phase 2</p>
            <h2 className="heading-2 mb-8">Independent Verification</h2>
            <p className="text-lg text-slate-700 mb-6">
              SAFE Labs introduces certification.
            </p>
            <p className="text-lg text-slate-700 mb-6">
              Systems are tested against the SAFE standard.
            </p>
            <p className="text-lg text-slate-700 mb-2">Outcomes are binary:</p>
            <p className="text-lg text-slate-900 font-semibold">pass or fail.</p>
          </div>
        </div>
      </section>

      {/* Phase 3 */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Phase 3</p>
            <h2 className="heading-2 mb-8">Early Adoption</h2>
            <p className="text-lg text-slate-700 mb-4">
              Organisations begin aligning to SAFE.
            </p>
            <p className="text-lg text-slate-700 mb-4">
              Certification becomes a signal of credibility.
            </p>
            <p className="text-lg text-slate-900 font-semibold">
              Unverified systems begin to face scrutiny.
            </p>
          </div>
        </div>
      </section>

      {/* Phase 4 */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Phase 4</p>
            <h2 className="heading-2 mb-8">Institutional Integration</h2>
            <p className="text-lg text-slate-700 mb-6">
              Regulators, insurers, and procurement frameworks adopt SAFE as a reference standard.
            </p>
            <p className="text-lg text-slate-900 font-semibold">
              Verification becomes a requirement, not a differentiator.
            </p>
          </div>
        </div>
      </section>

      {/* Phase 5 */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Phase 5</p>
            <h2 className="heading-2 mb-8">Market Expectation</h2>
            <p className="text-lg text-slate-700 mb-4">
              Independent verification becomes expected.
            </p>
            <p className="text-lg text-slate-700 mb-4">
              Safety claims without certification are no longer accepted.
            </p>
            <p className="text-lg text-slate-900 font-semibold">
              Unverified systems lose trust.
            </p>
          </div>
        </div>
      </section>

      {/* Pressure */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Pressure</p>
            <p className="text-lg text-slate-300 mb-6">As adoption increases:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-cyan-400 pl-6">
                <p className="text-lg text-slate-200">Uncertified systems face regulatory scrutiny</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-6">
                <p className="text-lg text-slate-200">Insurers assess risk based on certification status</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-6">
                <p className="text-lg text-slate-200">Institutions require verification for deployment</p>
              </div>
            </div>
            <p className="text-lg text-white font-semibold">
              Verification becomes the baseline.
            </p>
          </div>
        </div>
      </section>

      {/* Outcome */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Outcome</p>
            <p className="text-lg text-slate-700 mb-8">Safety transitions from:</p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-lg text-slate-400 line-through">self-declared</span>
                <span className="text-slate-500">&rarr;</span>
                <span className="text-lg text-slate-900 font-semibold">independently verified</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-lg text-slate-400 line-through">optional</span>
                <span className="text-slate-500">&rarr;</span>
                <span className="text-lg text-slate-900 font-semibold">required</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-lg text-slate-400 line-through">assumed</span>
                <span className="text-slate-500">&rarr;</span>
                <span className="text-lg text-slate-900 font-semibold">proven</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Line + CTA */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-12">
            If safety cannot be proven,<br />
            it will not be trusted.
          </p>
          <button
            onClick={() => setShowCertForm(true)}
            className="btn-primary"
            data-testid="btn-request-certification"
          >
            Request Certification
          </button>
        </div>
      </section>

      <CertificationInterestForm
        isOpen={showCertForm}
        onClose={() => setShowCertForm(false)}
      />
    </div>
  );
};

export default RoadmapPage;
